import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { getAllRoutes } from "../../config/routes";
import { useNotifications } from "../../contexts/NotificationsContext";

const TopNav = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const { unread, items, markAllRead, clearAll } = useNotifications();
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);

  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef(null);

  // ✅ Dark Mode (GLOBAL toggle)
  const [darkMode, setDarkMode] = useState(() => {
    const hasDarkClass = document.documentElement.classList.contains("dark");
    if (hasDarkClass) return true;
    return localStorage.getItem("theme") === "dark";
  });

  // ✅ Apply theme instantly
  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      root.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [darkMode]);

  const allRoutes = useMemo(() => getAllRoutes(), []);
  const profileRoute = allRoutes.find((item) => !item.showInNav && item.name === "Profile");

  // Close dropdown on outside click + ESC
  useEffect(() => {
    const onDown = (e) => {
      if (e.key === "Escape") {
        setIsProfileOpen(false);
        setNotifOpen(false);
      }
    };

    const onClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setIsProfileOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };

    document.addEventListener("keydown", onDown);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onDown);
      document.removeEventListener("mousedown", onClick);
    };
  }, []);

  const initial = (user?.name?.trim()?.[0] || "A").toUpperCase();

  const normalizeType = (t) => String(t || "").trim().toLowerCase();

  // ✅ Nice metadata for UI
  const typeMeta = (n) => {
    const type = normalizeType(n?.type);
    const source = String(n?.source || "").toLowerCase();

    if (source === "alerts") return { icon: "🚨", label: "Alert", chip: "bg-red-600 text-white" };

    if (type === "health_records")
      return { icon: "🩺", label: "Health", chip: "bg-emerald-600 text-white" };
    if (type === "development_milestones")
      return { icon: "🎯", label: "Milestone", chip: "bg-indigo-600 text-white" };
    if (type === "donations") return { icon: "💸", label: "Donation", chip: "bg-amber-600 text-white" };
    if (type === "system_updates") return { icon: "⚙️", label: "System", chip: "bg-slate-800 text-white" };
    if (type === "data_backup") return { icon: "🗄️", label: "Backup", chip: "bg-sky-600 text-white" };
    if (type === "user_activity") return { icon: "👤", label: "User", chip: "bg-fuchsia-600 text-white" };

    return { icon: "🔔", label: "Notice", chip: "bg-slate-600 text-white" };
  };

  // ✅ Clean relative time
  const timeAgo = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (!Number.isFinite(diff)) return "";
    if (diff < 60) return `${diff}s ago`;
    const m = Math.floor(diff / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const days = Math.floor(h / 24);
    return `${days}d ago`;
  };

  function defaultClickPath(n) {
    const type = normalizeType(n?.type);
    const source = String(n?.source || "").toLowerCase();
    const childId = n?.childId ?? n?.entityId ?? null;

    if (source === "alerts") {
      const id = n?.alertId ?? n?.entityId ?? null;
      if (id) return `/children/alerts?open=${id}`;
      return "/children/alerts";
    }

    if (type === "system_updates" || type === "data_backup") return "/setting";

    if (type === "health_records") {
      if (childId) return `/children?open=${childId}`;
      return "/children";
    }

    if (type === "development_milestones") {
      if (childId) return `/children?dev=${childId}`;
      return "/children/development";
    }

    if (type === "donations") {
      const id = n?.donationId ?? n?.entityId ?? null;
      if (id) return `/children/donations?view=donations&open=${id}`;
      return "/children/donations?view=donations";
    }

    if (type === "user_activity") return "/users";

    return "/notifications";
  }

  const onClickNotifRow = (n) => {
    navigate(defaultClickPath(n));
    setNotifOpen(false);
  };

  return (
    <header className="sticky top-0 z-40">
      <div className="bg-white/80 dark:bg-gray-950/70 backdrop-blur-xl border-b border-slate-200 dark:border-gray-800 transition-colors duration-300">
        <div className="px-2 lg:px-6 py-2">
          <div className="flex items-center justify-between gap-3">
            {/* Left */}
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={onMenuClick}
                className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-xl border border-slate-200 dark:border-gray-800
                           bg-white dark:bg-gray-900 hover:bg-slate-50 dark:hover:bg-gray-800
                           text-slate-700 dark:text-gray-200 transition
                           focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60"
                aria-label="Open menu"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>

            {/* Right */}
            <div className="flex items-center gap-2 lg:gap-3">
              {/* Notifications */}
              <div className="relative" ref={notifRef}>
                <button
                  onClick={() => setNotifOpen((v) => !v)}
                  className="inline-flex relative items-center justify-center h-10 w-10 rounded-xl border border-slate-200 dark:border-gray-800
                          bg-white dark:bg-gray-900 hover:bg-slate-50 dark:hover:bg-gray-800
                          text-slate-700 dark:text-gray-200 transition
                          focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60"
                  aria-label="Notifications"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0a3 3 0 11-6 0h6z"
                    />
                  </svg>

                  {unread > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[11px] font-bold grid place-items-center ring-2 ring-white dark:ring-gray-900">
                      {unread > 99 ? "99+" : unread}
                    </span>
                  )}
                </button>

                {notifOpen && (
                  <div
                    className="
                      fixed sm:absolute z-50 overflow-hidden shadow-2xl
                      rounded-2xl border border-slate-200 dark:border-gray-800
                      bg-white dark:bg-gray-900
                      inset-x-2 top-16
                      sm:inset-x-auto sm:top-auto sm:right-0 sm:mt-2
                      w-auto sm:w-96
                      max-w-[420px]
                    "
                  >
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-slate-100 dark:border-gray-800 flex items-center justify-between">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900 dark:text-gray-100">
                          Notifications
                        </p>
                        <p className="text-xs text-slate-500 dark:text-gray-400">
                          {unread > 0 ? `${unread} unread` : "You're all caught up"}
                        </p>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={markAllRead}
                          className="text-xs px-2 py-1 rounded-lg text-slate-700 dark:text-gray-200 hover:bg-slate-50 dark:hover:bg-gray-800"
                        >
                          Mark all
                        </button>
                        <button
                          onClick={clearAll}
                          className="text-xs px-2 py-1 rounded-lg text-slate-700 dark:text-gray-200 hover:bg-slate-50 dark:hover:bg-gray-800"
                        >
                          Clear
                        </button>
                      </div>
                    </div>

                    {/* List */}
                    <div className="max-h-[60vh] sm:max-h-[360px] overflow-auto">
                      {items.length === 0 ? (
                        <div className="p-6 text-center">
                          <div className="mx-auto h-12 w-12 rounded-2xl bg-slate-100 dark:bg-gray-800 grid place-items-center text-xl">
                            🔔
                          </div>
                          <p className="mt-3 text-sm font-semibold text-slate-900 dark:text-gray-100">
                            No notifications
                          </p>
                          <p className="mt-1 text-sm text-slate-600 dark:text-gray-300">
                            Updates will appear here in real-time.
                          </p>
                        </div>
                      ) : (
                        items.slice(0, 20).map((n) => {
                          const key = n.dedupeKey || n.id || `${n.type}:${n.createdAt}:${n.title}`;
                          const meta = typeMeta(n);
                          const when = timeAgo(n.createdAt || n.timestamp);

                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => onClickNotifRow(n)}
                              className={`group w-full text-left px-4 py-3 border-b border-slate-100 dark:border-gray-800
                                hover:bg-slate-50 dark:hover:bg-gray-800/40 transition
                                ${n.read ? "" : "bg-blue-50/40 dark:bg-blue-950/10 border-l-4 border-l-blue-600"}`}
                            >
                              <div className="flex items-start gap-3">
                                {/* icon */}
                                <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700 dark:bg-gray-800 dark:text-gray-200">
                                  <span className="text-lg">{meta.icon}</span>
                                </div>

                                <div className="min-w-0 flex-1">
                                  <div className="flex items-start justify-between gap-2">
                                    <p className="truncate text-sm font-semibold text-slate-900 dark:text-gray-100">
                                      {n.title || meta.label}
                                    </p>

                                    <span className="shrink-0 text-xs text-slate-400 dark:text-gray-400">
                                      {when || ""}
                                    </span>
                                  </div>

                                  {n.message ? (
                                    <p className="mt-1 line-clamp-2 text-sm text-slate-600 dark:text-gray-300">
                                      {n.message}
                                    </p>
                                  ) : null}

                                  <div className="mt-2 flex items-center gap-2">
                                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${meta.chip}`}>
                                      {meta.label}
                                    </span>

                                    {!n.read ? (
                                      <span className="inline-flex items-center rounded-full bg-blue-600 px-2 py-0.5 text-xs font-semibold text-white">
                                        New
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 dark:bg-gray-800 dark:text-gray-200">
                                        Read
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>

                    {/* Footer */}
                    <div className="px-4 py-3 border-t border-slate-100 dark:border-gray-800 flex items-center justify-between">
                      <button
                        onClick={() => {
                          setNotifOpen(false);
                          markAllRead();
                        }}
                        className="text-sm font-medium text-slate-700 hover:text-slate-900 dark:text-gray-300 dark:hover:text-white"
                      >
                        Mark read
                      </button>

                      <button
                        onClick={() => {
                          navigate("/notifications");
                          setNotifOpen(false);
                        }}
                        className="text-sm font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-300 dark:hover:text-blue-200"
                      >
                        View all →
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Profile */}
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setIsProfileOpen((v) => !v)}
                  className="inline-flex items-center gap-2 rounded-xl p-1.5 pr-2 hover:bg-slate-50 dark:hover:bg-gray-800 transition"
                >
                  <div className="h-9 w-9 rounded-full overflow-hidden bg-gradient-to-br from-blue-600 to-indigo-600 grid place-items-center shadow-sm">
                    {user?.avatarUrl ? (
                      <img
                        src={
                          user.avatarUrl.startsWith("http")
                            ? user.avatarUrl
                            : `${(process.env.REACT_APP_API_BASE || "http://localhost:5000").replace(
                                /\/+$/,
                                ""
                              )}${user.avatarUrl}`
                        }
                        alt="Profile"
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <span className="text-white text-sm font-semibold">{initial}</span>
                    )}
                  </div>

                  <div className="hidden lg:block text-left leading-tight">
                    <p className="text-sm font-semibold truncate text-slate-900 dark:text-gray-100">
                      {user?.name || "Admin"}
                    </p>
                    <p className="text-xs truncate text-slate-500 dark:text-gray-400">
                      {user?.email || "Administrator"}
                    </p>
                  </div>
                </button>

                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded-2xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-xl overflow-hidden z-50">
                    <div className="px-4 py-3 border-b border-slate-100 dark:border-gray-800">
                      <p className="text-sm font-semibold truncate text-slate-900 dark:text-gray-100">
                        {user?.name || "Admin"}
                      </p>
                      <p className="text-xs truncate text-slate-500 dark:text-gray-400">
                        {user?.email || "Administrator"}
                      </p>
                    </div>

                    <div className="py-2">
                      {profileRoute && (
                        <Link
                          to={profileRoute.path}
                          className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 dark:text-gray-200 hover:bg-slate-50 dark:hover:bg-gray-800"
                          onClick={() => setIsProfileOpen(false)}
                        >
                          Profile Settings
                        </Link>
                      )}

                      <div className="flex items-center justify-between px-4 py-2.5">
                        <span className="text-sm text-slate-700 dark:text-gray-200">Dark Mode</span>

                        <button
                          onClick={() => setDarkMode((v) => !v)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                            darkMode ? "bg-indigo-600" : "bg-gray-300"
                          }`}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
                              darkMode ? "translate-x-5" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </div>

                      <div className="my-1 border-t border-slate-100 dark:border-gray-800" />

                      <button
                        onClick={() => {
                          logout();
                          setIsProfileOpen(false);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 dark:text-gray-200 hover:bg-slate-50 dark:hover:bg-gray-800"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* end right */}
          </div>
        </div>
      </div>
    </header>
  );
};

export default TopNav;